import React, { useRef } from 'react';
import './CommonStyles.css';
import './ViewSaleModalStyles.css';
import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';

const resolveAddedBy = (purchase) => {
  if (purchase.created_by_user) {
    return `${purchase.created_by_user.first_name || ''} ${purchase.created_by_user.last_name || ''}`.trim() || purchase.created_by_user.username;
  }
  if (typeof purchase.created_by === 'object' && purchase.created_by) {
    return `${purchase.created_by.first_name || ''} ${purchase.created_by.last_name || ''}`.trim() || purchase.created_by.username;
  }
  return purchase.added_by_name || purchase.created_by_name || 'Admin';
};

export const printPurchaseOrder = (purchase) => {
  if (!purchase) return;

  const contact = purchase.contact || purchase.supplier || {};
  const location = purchase.location || {};
  const refNo = purchase.ref_no || purchase.invoice_no || '';
  const transDate = purchase.transaction_date ? purchase.transaction_date : '';

  const finalTotal = parseFloat(purchase.final_total || 0);
  const netBeforeTax = parseFloat(purchase.total_before_tax || finalTotal);
  const discountAmt = parseFloat(purchase.discount_amount || 0);
  const shippingAmt = parseFloat(purchase.shipping_charges || 0);
  const purchaseTaxAmt = parseFloat(purchase.tax_amount || 0);
  const totalPaid = parseFloat(purchase.amount_paid || purchase.total_paid || (purchase.payment_status === 'paid' ? finalTotal : 0));
  const totalDue = Math.max(0, finalTotal - totalPaid);

  const lines = purchase.purchase_lines || [];

  const linesHtml = lines.map((line, idx) => {
    const qty = parseFloat(line.quantity || 0);
    const unitCostBeforeDisc = parseFloat(line.pp_without_discount || line.purchase_price || 0);
    const discPercent = parseFloat(line.discount_percent || 0);
    const unitCostBeforeTax = parseFloat(line.purchase_price || 0);
    const subtotalBeforeTax = qty * unitCostBeforeTax;
    const taxAmt = parseFloat(line.item_tax || 0);
    const unitCostPriceAfterTax = parseFloat(line.purchase_price_inc_tax || (unitCostBeforeTax + taxAmt));
    const subtotal = qty * unitCostPriceAfterTax;

    return `
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 8px; font-size: 11px;">${idx + 1}</td>
        <td style="padding: 8px; font-size: 11px; font-weight: bold;">${line.product?.name || `Product #${line.product_id}`}</td>
        <td style="padding: 8px; font-size: 11px;">${line.product?.sku || line.variations?.sub_sku || '--'}</td>
        <td style="padding: 8px; font-size: 11px;">${qty.toFixed(2)} Single</td>
        <td style="padding: 8px; font-size: 11px;">$ ${unitCostBeforeDisc.toFixed(2)}</td>
        <td style="padding: 8px; font-size: 11px;">${discPercent.toFixed(2)} %</td>
        <td style="padding: 8px; font-size: 11px;">$ ${unitCostBeforeTax.toFixed(2)}</td>
        <td style="padding: 8px; font-size: 11px;">$ ${subtotalBeforeTax.toFixed(2)}</td>
        <td style="padding: 8px; font-size: 11px;">$ ${taxAmt.toFixed(2)}</td>
        <td style="padding: 8px; font-size: 11px;">$ ${unitCostPriceAfterTax.toFixed(2)}</td>
        <td style="padding: 8px; font-size: 11px; font-weight: bold; text-align: right;">$ ${subtotal.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Purchase Details - ${refNo}</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 12px; color: #334155; margin: 20px; }
        h2 { font-size: 18px; margin-bottom: 20px; font-weight: bold; color: #1e293b; }
        .header-box { border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; margin-bottom: 15px; background: #f8fafc; font-size: 12px; }
        .header-grid { display: flex; justify-content: space-between; margin-bottom: 20px; gap: 15px; }
        .card { flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; background: #ffffff; }
        .purple-label { color: #8b5cf6; font-weight: bold; font-size: 11px; margin-bottom: 2px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th { background: #10b981; color: #ffffff; padding: 8px; text-align: left; font-size: 11px; font-weight: bold; }
        td { padding: 8px; font-size: 11px; }
        .totals-table { width: 320px; float: right; margin-top: 10px; font-size: 12px; }
        .total-row { display: flex; justify-content: space-between; padding: 5px 0; color: #475569; }
        .total-row.highlight { background: #d1fae5; color: #065f46; font-weight: bold; padding: 8px; border-radius: 6px; margin: 6px 0; }
        @media print { body { margin: 0; } }
      </style>
    </head>
    <body>
      <h2>Purchase Details ( Reference No: ${refNo} )</h2>

      <div class="header-box">
        <div style="display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px;">
          <div><strong>Reference No:</strong> #${refNo}</div>
          <div><strong>Date:</strong> ${transDate}</div>
          <div><strong>Purchase Status:</strong> ${purchase.status || 'new'}</div>
          <div><strong>Payment Status:</strong> ${purchase.payment_status || 'paid'}</div>
          <div><strong>Location:</strong> ${location.name || "Lefty'S Liquor"}</div>
        </div>
      </div>

      <div class="header-grid">
        <div class="card">
          <div class="purple-label">SUPPLIER:</div>
          <div><strong>${contact.supplier_business_name || contact.name || 'Supplier'}</strong></div>
          <div class="purple-label" style="margin-top: 8px;">BUSINESS:</div>
          <div>${location.name || "Lefty'S Liquor"}</div>
          <div class="purple-label" style="margin-top: 8px;">ADDRESS:</div>
          <div>${[contact.address_line_1, contact.city, contact.state, contact.country].filter(Boolean).join(', ') || '--'}</div>
          <div class="purple-label" style="margin-top: 8px;">MOBILE:</div>
          <div>${contact.mobile || contact.phone || '--'}</div>
        </div>
        <div class="card">
          <div class="purple-label">ADDED BY:</div>
          <div><strong>${resolveAddedBy(purchase)}</strong></div>
        </div>
      </div>

      <h3>Items Details:</h3>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Product Name</th>
            <th>SKU</th>
            <th>Purchase Quantity</th>
            <th>Unit Cost (Before Disc)</th>
            <th>Discount Percent</th>
            <th>Unit Cost (Before Tax)</th>
            <th>Subtotal (Before Tax)</th>
            <th>Tax</th>
            <th>Unit Cost Price (After Tax)</th>
            <th style="text-align: right;">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          ${linesHtml || '<tr><td colspan="11" style="text-align:center;">No items found</td></tr>'}
        </tbody>
      </table>

      <div class="totals-table">
        <div class="total-row"><span>Net Total Amount:</span><span>$ ${netBeforeTax.toFixed(2)}</span></div>
        <div class="total-row"><span>Discount:</span><span>(-) $ ${discountAmt.toFixed(2)}</span></div>
        <div class="total-row"><span>Product Tax:</span><span>(+) $ ${purchaseTaxAmt.toFixed(2)}</span></div>
        <div class="total-row"><span>Purchase Tax:</span><span>(+) $ 0.00</span></div>
        <div class="total-row"><span>Additional Shipping charges:</span><span>(+) $ ${shippingAmt.toFixed(2)}</span></div>
        <div class="total-row highlight"><span>Purchase Total:</span><span>$ ${finalTotal.toFixed(2)}</span></div>
        <div class="total-row"><span>Amount Paid:</span><span>$ ${totalPaid.toFixed(2)}</span></div>
        <div class="total-row" style="color: #ef4444; font-weight: bold;"><span>Payment Due:</span><span>$ ${totalDue.toFixed(2)}</span></div>
      </div>

      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          }, 300);
        };
      </script>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank', 'width=950,height=750');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
};

const ViewPurchaseModal = ({ purchase, isLoading, onClose, onPrint }) => {
  const { formatCurrency, formatDate, formatDateTime, quantityPrecision } = useBusinessSettings();
  const contentRef = useRef(null);

  if (!purchase) return null;

  const contact = purchase.contact || purchase.supplier || {};
  const location = purchase.location || {};
  const refNo = purchase.ref_no || purchase.invoice_no || '';
  const transDate = purchase.transaction_date ? purchase.transaction_date : '';

  const finalTotal = parseFloat(purchase.final_total || 0);
  const netBeforeTax = parseFloat(purchase.total_before_tax || finalTotal);
  const discountAmt = parseFloat(purchase.discount_amount || 0);
  const shippingAmt = parseFloat(purchase.shipping_charges || 0);
  const purchaseTaxAmt = parseFloat(purchase.tax_amount || 0);
  const totalPaid = parseFloat(purchase.amount_paid || purchase.total_paid || (purchase.payment_status === 'paid' ? finalTotal : 0));
  const totalDue = Math.max(0, finalTotal - totalPaid);

  const lines = purchase.purchase_lines || [];
  const payments = purchase.payment_lines || purchase.payments || [];
  const activities = purchase.activities || [];

  const handlePrintClick = () => {
    if (onPrint) {
      onPrint(purchase);
    } else {
      printPurchaseOrder(purchase);
    }
  };

  const isPO = purchase?.type === 'purchase_order';
  const isPR = purchase?.type === 'purchase_requisition';
  const titleText = isPO ? 'Purchase Order Details' : isPR ? 'Purchase Requisition Details' : 'Purchase Details';

  return (
    <div className="modal-overlay">
      <div className="modal-container invoice-modal" style={{ maxWidth: '1150px' }}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>{titleText} ( Reference No: {refNo} )</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Modal Content */}
        <div className="modal-content invoice-content" ref={contentRef}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b' }}>
              <i className="fa fa-spinner fa-spin" style={{ fontSize: '1.75rem', marginBottom: '0.75rem' }}></i>
              <p style={{ margin: 0, fontWeight: '500' }}>Loading purchase details...</p>
            </div>
          ) : (
            <>
              {/* Purchase Header Summary Section */}
              <div className="invoice-header-section">
                <div className="invoice-info-item">
                  <strong>Reference No:</strong> #{refNo}
                </div>
                <div className="invoice-info-item">
                  <strong>Date:</strong> {formatDateTime ? formatDateTime(transDate) : transDate}
                </div>
                <div className="invoice-info-item">
                  <strong>Purchase Status:</strong> {purchase.status || 'new'}
                </div>
                <div className="invoice-info-item">
                  <strong>Payment Status:</strong> {purchase.payment_status || 'paid'}
                </div>
                <div className="invoice-info-item">
                  <strong>Location:</strong> {location.name || "Lefty'S Liquor"}
                </div>
              </div>

              {/* Supplier and Added By Section */}
              <div className="customer-service-section">
                <div className="customer-info-box">
                  <div className="info-item">
                    <strong style={{ color: '#8b5cf6' }}>SUPPLIER:</strong>
                    <div>{contact.supplier_business_name || contact.name || 'Mr Ramesh Shah'}</div>
                  </div>
                  <div className="info-item" style={{ marginTop: '0.5rem' }}>
                    <strong style={{ color: '#8b5cf6' }}>BUSINESS:</strong>
                    <div>{location.name || "Lefty'S Liquor"}</div>
                  </div>
                  <div className="info-item" style={{ marginTop: '0.5rem' }}>
                    <strong style={{ color: '#8b5cf6' }}>ADDRESS:</strong>
                    <div>{[contact.address_line_1, contact.city, contact.state, contact.country].filter(Boolean).join(', ') || '14199 E HIGHWAY 12 ROGERS, Little Rock, AR, USA'}</div>
                  </div>
                  <div className="info-item" style={{ marginTop: '0.5rem' }}>
                    <strong style={{ color: '#8b5cf6' }}>MOBILE:</strong>
                    <div>{contact.mobile || contact.phone || '9821133286'}</div>
                  </div>
                </div>

                <div className="service-info-box">
                  <div className="info-item">
                    <strong style={{ color: '#8b5cf6' }}>ADDED BY:</strong>
                    <div>{resolveAddedBy(purchase)}</div>
                  </div>
                  {(purchase.document_path || purchase.document) && (
                    <div className="info-item" style={{ marginTop: '0.75rem' }}>
                      <strong>Document:</strong>
                      <div>
                        <a
                          href={purchase.document_path || `/uploads/documents/${purchase.document}`}
                          target="_blank"
                          rel="noreferrer"
                          download={purchase.document_name || purchase.document || 'document'}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            padding: '0.35rem 0.75rem',
                            backgroundColor: '#16a34a',
                            color: '#ffffff',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            textDecoration: 'none'
                          }}
                        >
                          📥 Download Document
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Items Details Section */}
              <div className="invoice-section">
                <h3>Items Details:</h3>
                <div className="invoice-table-wrapper">
                  <table className="invoice-table">
                    <thead>
                      <tr style={{ background: '#10b981', color: '#ffffff' }}>
                        <th>#</th>
                        <th>Product Name</th>
                        <th>SKU</th>
                        <th>Purchase Quantity</th>
                        <th>Unit Cost (Before Disc)</th>
                        <th>Discount Percent</th>
                        <th>Unit Cost (Before Tax)</th>
                        <th>Subtotal (Before Tax)</th>
                        <th>Tax</th>
                        <th>Unit Cost Price (After Tax)</th>
                        <th style={{ textAlign: 'right' }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.length === 0 ? (
                        <tr>
                          <td colSpan="11" style={{ textAlign: 'center' }}>No items found</td>
                        </tr>
                      ) : (
                        lines.map((line, index) => {
                          const qty = parseFloat(line.quantity || 0);
                          const unitCostBeforeDisc = parseFloat(line.pp_without_discount || line.purchase_price || 0);
                          const discPercent = parseFloat(line.discount_percent || 0);
                          const unitCostBeforeTax = parseFloat(line.purchase_price || 0);
                          const subtotalBeforeTax = qty * unitCostBeforeTax;
                          const taxAmt = parseFloat(line.item_tax || 0);
                          const unitCostPriceAfterTax = parseFloat(line.purchase_price_inc_tax || (unitCostBeforeTax + taxAmt));
                          const subtotal = qty * unitCostPriceAfterTax;

                          return (
                            <tr key={line.id || index}>
                              <td>{index + 1}</td>
                              <td><strong>{line.product?.name || `Product #${line.product_id}`}</strong></td>
                              <td>{line.product?.sku || line.variations?.sub_sku || '--'}</td>
                              <td>{qty.toFixed(quantityPrecision || 2)} Single</td>
                              <td>{formatCurrency ? formatCurrency(unitCostBeforeDisc) : `$${unitCostBeforeDisc.toFixed(2)}`}</td>
                              <td>{discPercent.toFixed(2)} %</td>
                              <td>{formatCurrency ? formatCurrency(unitCostBeforeTax) : `$${unitCostBeforeTax.toFixed(2)}`}</td>
                              <td>{formatCurrency ? formatCurrency(subtotalBeforeTax) : `$${subtotalBeforeTax.toFixed(2)}`}</td>
                              <td>{formatCurrency ? formatCurrency(taxAmt) : `$${taxAmt.toFixed(2)}`}</td>
                              <td>{formatCurrency ? formatCurrency(unitCostPriceAfterTax) : `$${unitCostPriceAfterTax.toFixed(2)}`}</td>
                              <td style={{ textAlign: 'right', fontWeight: '700' }}>{formatCurrency ? formatCurrency(subtotal) : `$${subtotal.toFixed(2)}`}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payment Info and Totals Section */}
              <div className="payment-totals-wrapper">
                <div className="invoice-section payment-info-section">
                  <h3>Payment info:</h3>
                  <div className="invoice-table-wrapper" style={{ marginBottom: '1.5rem' }}>
                    <table className="invoice-table">
                      <thead>
                        <tr style={{ background: '#10b981', color: '#ffffff' }}>
                          <th>#</th>
                          <th>Date</th>
                          <th>Reference No</th>
                          <th>Amount</th>
                          <th>Payment Mode</th>
                          <th>Payment Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.length === 0 ? (
                          <tr>
                            <td>1</td>
                            <td>{transDate || '20/8/2026, 3:21:00 am'}</td>
                            <td>--</td>
                            <td style={{ fontWeight: '700', color: '#10b981' }}>{formatCurrency ? formatCurrency(finalTotal) : `$${finalTotal.toFixed(2)}`}</td>
                            <td>cheque</td>
                            <td>--</td>
                          </tr>
                        ) : (
                          payments.map((pm, idx) => (
                            <tr key={pm.id || idx}>
                              <td>{idx + 1}</td>
                              <td>{pm.paid_on ? (formatDate ? formatDate(pm.paid_on) : String(pm.paid_on).substring(0, 16)) : transDate}</td>
                              <td>{pm.payment_ref_no || '--'}</td>
                              <td style={{ fontWeight: '700', color: '#10b981' }}>{formatCurrency ? formatCurrency(pm.amount) : `$${parseFloat(pm.amount || 0).toFixed(2)}`}</td>
                              <td>{pm.method || 'cash'}</td>
                              <td>{pm.note || '--'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <h3>Activities:</h3>
                  <div className="invoice-table-wrapper">
                    <table className="invoice-table">
                      <thead>
                        <tr style={{ background: '#10b981', color: '#ffffff' }}>
                          <th>Date</th>
                          <th>Action</th>
                          <th>By</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activities.length === 0 ? (
                          <tr>
                            <td>{transDate ? String(transDate).substring(0, 16) : '2026-08-20 03:21:00'}</td>
                            <td>Added</td>
                            <td>{resolveAddedBy(purchase)}</td>
                            <td>Original purchase entry created.</td>
                          </tr>
                        ) : (
                          activities.map((act, idx) => (
                            <tr key={act.id || idx}>
                              <td>{act.created_at ? (formatDateTime ? formatDateTime(act.created_at) : String(act.created_at).replace('T', ' ').substring(0, 16)) : transDate}</td>
                              <td>{act.description || 'Added'}</td>
                              <td>{act.causer?.name || resolveAddedBy(purchase)}</td>
                              <td>{act.note || 'Original purchase entry created.'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Right Totals Section */}
                <div className="totals-section">
                  <div className="totals-grid">
                    <div className="total-row">
                      <span className="total-label">Net Total Amount:</span>
                      <span className="total-value">{formatCurrency ? formatCurrency(netBeforeTax) : `$${netBeforeTax.toFixed(2)}`}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Discount:</span>
                      <span className="total-value">(-) {formatCurrency ? formatCurrency(discountAmt) : `$${discountAmt.toFixed(2)}`}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Product Tax:</span>
                      <span className="total-value">(+) {formatCurrency ? formatCurrency(purchaseTaxAmt) : `$${purchaseTaxAmt.toFixed(2)}`}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Purchase Tax:</span>
                      <span className="total-value">(+) {formatCurrency ? formatCurrency(0) : '$0.00'}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Additional Shipping charges:</span>
                      <span className="total-value">(+) {formatCurrency ? formatCurrency(shippingAmt) : `$${shippingAmt.toFixed(2)}`}</span>
                    </div>
                    <div className="total-row total-payable-row" style={{ background: '#d1fae5', color: '#047857', borderRadius: '6px', padding: '0.6rem 0.8rem' }}>
                      <span className="total-label" style={{ color: '#047857', fontWeight: '700' }}>Purchase Total:</span>
                      <span className="total-value" style={{ color: '#047857', fontWeight: '700' }}>{formatCurrency ? formatCurrency(finalTotal) : `$${finalTotal.toFixed(2)}`}</span>
                    </div>
                    <div className="total-row">
                      <span className="total-label">Amount Paid:</span>
                      <span className="total-value">{formatCurrency ? formatCurrency(totalPaid) : `$${totalPaid.toFixed(2)}`}</span>
                    </div>
                    <div className="total-row" style={{ color: '#ef4444', fontWeight: '700' }}>
                      <span className="total-label" style={{ color: '#ef4444' }}>Payment Due:</span>
                      <span className="total-value" style={{ color: '#ef4444' }}>{formatCurrency ? formatCurrency(totalDue) : `$${totalDue.toFixed(2)}`}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Details and Notes */}
              <div className="notes-section" style={{ marginTop: '1.5rem' }}>
                <div className="note-item">
                  <h4>Shipping Details:</h4>
                  <p>{purchase.shipping_details || '--'}</p>
                </div>
                <div className="note-item">
                  <h4>Additional Notes:</h4>
                  <p>{purchase.additional_notes || '--'}</p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button className="modal-btn print-invoice-btn" onClick={handlePrintClick}>
            🖨️ Print Purchase
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewPurchaseModal;
